// @ts-check
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

import simpleHash from '@/utils/simpleHash';
import { useEnvelope } from '@/composables/envelopes';

/**
 * Get route name and all child route names recursively
 *
 * @param {import('vue-router').RouteConfig} route
 */
function getRouteNames(route) {
  return route.name + route.children?.map(getRouteNames).join();
}
/**
 * Steps routes
 *
 * provides step routes and current step index,
 * calculates previous and next routes for "steps" pages,
 * cancel and update routes for "edit" pages
 * progress route for resuming a draft
 *
 * @param {string} routeName containing the steps routes
 */
function useStepRoutes(routeName) {
  const router = useRouter();

  const stepsRoutes =
    router.options.routes
      .find(route => route.name === routeName)
      .children.filter(route => route.name) || [];

  const stepsHash = simpleHash(stepsRoutes.map(getRouteNames).join());

  return { stepsRoutes, stepsHash };
}

function useStepRouteDetails() {
  const currentRoute = useRoute();

  const parentRoute = computed(() => currentRoute.matched.at(-2));
  const isCreateRoute = computed(() => currentRoute.name?.endsWith('Create'));
  const isEditStepRoute = computed(() => !!currentRoute.params.isEditStep);
  const isEditRoute = computed(() => parentRoute.value?.name?.endsWith('Edit'));
  const isStepsRoute = computed(() =>
      currentRoute.name?.endsWith('Steps') ||
      parentRoute.value?.name?.endsWith('Steps'));

  return {
    isCreateRoute,
    isEditRoute,
    isEditStepRoute,
    isStepsRoute,
  };
}

export function useEnvelopesRoutes() {
  const currentRoute = useRoute();
  const { stepsRoutes, stepsHash } = useStepRoutes('EnvelopesSteps');
  const {
    isEditRoute, isEditStepRoute, isStepsRoute, isCreateRoute,
  } = useStepRouteDetails();

  const { stepsProgress, isSent } = useEnvelope(currentRoute.params.fingerprint);

  const currentIndex = computed(() =>
    stepsRoutes.findIndex(route => route.name === currentRoute.name));
  const summaryIndex = stepsRoutes.findIndex(route => route.name === 'EnvelopesStepSummary');

  /**
   * Progress update to POST
   */
  const progressUpdate = computed(() => ({
    hash: stepsHash,
    index: currentIndex.value,
  }));

  /**
   * Progress route
   *
   * redirect to this route to ensure all steps are progressed through in order
   */
  const progressRoute = computed(() => {
    const { hash, index } = stepsProgress.value || {};
    const progressIndex = index + 1;

    // no step in url go to next incomplete step
    // step in url is before go to it instead
    const nextIndex =
      currentIndex.value < 0
        ? progressIndex
        : Math.min(currentIndex.value, progressIndex);

    // envelope has been sent so summary only
    if (isSent.value) {
      return stepsRoutes[summaryIndex];
    }

    // hashes don't match, steps have changed so go to beginning
    if (hash !== stepsHash) {
      return stepsRoutes[1];
    }

    // allow edit routes after reaching summary
    if (isEditRoute.value && progressIndex >= summaryIndex) {
      return currentRoute;
    }

    return stepsRoutes[nextIndex];
  });

  const prevRoute = computed(() => {
    if (isEditRoute.value || isEditStepRoute.value) {
      return stepsRoutes[summaryIndex];
    }

    if (isStepsRoute.value) {
      const route = stepsRoutes[currentIndex.value - 1];
      return route && { name: route.name };
    }

    return undefined;
  });

  const nextRoute = computed(() => {
    if (isCreateRoute.value) {
      return {
        name: stepsRoutes[1].name,
        params: { fingerprint: 'fingerprint' },
      };
    }

    if (isEditRoute.value) {
      return stepsRoutes[summaryIndex];
    }

    if (isStepsRoute.value) {
      const route = stepsRoutes[currentIndex.value + 1];
      return route && { name: route.name };
    }

    return undefined;
  });

  return {
    prevRoute,
    nextRoute,
    progressRoute,
    stepsRoutes,
    currentIndex,
    progressUpdate,
    isStepsRoute,
    isEditRoute,
    isCreateRoute,
    isEditStepRoute,
  };
}
